import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Admin.css';
import apiClient from '../utils/apiClient';
import AdminSettings from './AdminSettings';

const Admin = () => {
  // 메인 탭 상태 관리
  const [activeMainTab, setActiveMainTab] = useState('users'); // 'users' | 'settings'
  
  // 상태 관리
  const [users, setUsers] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [lastSearchKeyword, setLastSearchKeyword] = useState(''); // 마지막 검색에 사용된 키워드
  const [isSearching, setIsSearching] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // 페이징 관련 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // 체크박스 관련 상태
  const [selectAll, setSelectAll] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  
  // 모달 폼 데이터
  const [modalForm, setModalForm] = useState({
    userId: '',
    name: '',
    password: ''
  });

  // AbortController 참조
  const abortControllerRef = useRef(null);

  // 검색 핸들러
  const handleSearch = useCallback(async () => {
    // 이전 요청이 있으면 취소
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // 새로운 AbortController 생성
    abortControllerRef.current = new AbortController();
    
    setIsSearching(true);
    
    try {
      // 검색 파라미터 준비
      const params = {
        limit: itemsPerPage.toString(),
        page: currentPage.toString()
      };
      
      if (lastSearchKeyword.trim()) {
        params.name = lastSearchKeyword.trim();
      }
      
      // 백엔드 API 호출
      const response = await apiClient.get('/Account/GetUserList', params, abortControllerRef.current.signal);
      
      if (response && response.data) {
        setUsers(response.data);
        
        // 페이징 정보 업데이트
        setTotalCount(response.totalCount || 0);
        setTotalPages(Math.ceil((response.totalCount || 0) / itemsPerPage));
        
        // 현재 페이지 동기화 (서버에서 받은 페이지 정보가 있다면)
        if (response.nowPage) {
          setCurrentPage(response.nowPage);
        }
        
        // 체크박스 상태 초기화
        setSelectedItems([]);
        setSelectAll(false);
      } else {
        setUsers([]);
        setTotalCount(0);
        setTotalPages(0);
      }
      
    } catch (error) {
      
      // AbortError가 아닌 경우에만 알림 표시
      if (error.name !== 'AbortError' && !error.message?.includes('aborted')) {
        alert('사용자 검색에 실패했습니다.');
      }
      
      setUsers([]);
    } finally {
      setIsSearching(false);
    }
  }, [currentPage, itemsPerPage, lastSearchKeyword]);

  // 컴포넌트 마운트 시 전체 사용자 조회 및 페이지/검색 변경 시 재조회
  useEffect(() => {
    handleSearch();
    
    // 컴포넌트 언마운트 시 요청 취소
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [handleSearch]);

  // 검색 입력 핸들러
  const handleSearchKeywordChange = (e) => {
    setSearchKeyword(e.target.value);
  };

  // 검색 버튼 클릭 핸들러
  const handleSearchClick = () => {
    // 검색 키워드 업데이트
    setLastSearchKeyword(searchKeyword);
    // 1페이지로 이동 (useEffect에서 자동 검색 실행)
    setCurrentPage(1);
  };

  // Enter 키 핸들러
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      // 검색 키워드 업데이트
      setLastSearchKeyword(searchKeyword);
      // 1페이지로 이동 (useEffect에서 자동 검색 실행)
      setCurrentPage(1);
    }
  };

  // 모달 열기
  const handleAddClick = () => {
    setIsModalOpen(true);
    setModalForm({
      userId: '',
      name: '',
      password: ''
    });
  };

  // 모달 닫기
  const handleModalClose = () => {
    setIsModalOpen(false);
    setModalForm({
      userId: '',
      name: '',
      password: ''
    });
  };

  // 모달 폼 변경 핸들러
  const handleModalFormChange = (field, value) => {
    setModalForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 사용자 추가 핸들러
  const handleModalSubmit = async () => {
    // 유효성 검사
    if (!modalForm.userId.trim()) {
      alert('아이디를 입력해주세요.');
      return;
    }
    
    if (!modalForm.name.trim()) {
      alert('이름을 입력해주세요.');
      return;
    }
    
    if (!modalForm.password.trim()) {
      alert('패스워드를 입력해주세요.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      
      const userData = {
        account: modalForm.userId.trim(),
        name: modalForm.name.trim(),
        password: modalForm.password.trim()
      };
      
      const response = await apiClient.post('/Account/SignUp', userData);
      
      if (response && response.success){
        alert('사용자가 성공적으로 추가되었습니다.');
      
        // 모달 닫기
        handleModalClose();
        handleSearch();
      }else{
        alert(response.message);
      }
      
    } catch (error) {
      let errorMessage = '사용자 추가에 실패했습니다.';
      if (error.message) {
        errorMessage += `\n오류: ${error.message}`;
      }
      
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 날짜 포맷팅
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // 페이징 렌더링
  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 10;
    
    // 이전 버튼
    pages.push(
      <button
        key="prev"
        className="pagination-btn"
        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
        disabled={currentPage === 1}
      >
        &lt;
      </button>
    );

    // 페이지 번호들
    for (let i = 1; i <= Math.min(totalPages, maxVisiblePages); i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${currentPage === i ? 'active' : ''}`}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </button>
      );
    }

    // 다음 버튼
    pages.push(
      <button
        key="next"
        className="pagination-btn"
        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
        disabled={currentPage === totalPages}
      >
        &gt;
      </button>
    );

    return pages;
  };

  // 체크박스 관련 핸들러
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedItems(users.map(user => user.idx || user.id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (id, checked) => {
    if (checked) {
      const newSelectedItems = [...selectedItems, id];
      setSelectedItems(newSelectedItems);
      
      // 모든 항목이 선택되었는지 확인
      const allUserIds = users.map(user => user.idx || user.id);
      if (newSelectedItems.length === allUserIds.length) {
        setSelectAll(true);
      }
    } else {
      setSelectedItems(prev => prev.filter(item => item !== id));
      setSelectAll(false);
    }
  };

  // 개별 사용자 삭제
  const handleDeleteUser = async (user) => {
    const userId = user.idx || user.id;
    const userName = user.name || user.account || 'Unknown';
    
    if (!window.confirm(`'${userName}' 사용자를 삭제하시겠습니까?`)) {
      return;
    }

    try {
      const response = await apiClient.post('/Account/DeleteUser', [userId]);
      
      if (response && response.success){
        alert('사용자가 삭제되었습니다.');
      
        // 목록 재조회
        handleSearch();
      }else{
        alert(response.message);
      }
      
      
    } catch (error) {
      let errorMessage = '사용자 삭제에 실패했습니다.';
      if (error.message) {
        errorMessage += `\n오류: ${error.message}`;
      }
      
      alert(errorMessage);
    }
  };

  // 선택된 사용자들 삭제
  const handleDeleteSelected = async () => {
    if (selectedItems.length === 0) {
      alert('삭제할 사용자를 선택해주세요.');
      return;
    }

    if (!window.confirm(`선택된 ${selectedItems.length}명의 사용자를 삭제하시겠습니까?`)) {
      return;
    }

    try {
      const response = await apiClient.post('/Account/DeleteUser', selectedItems);
      
      if (response && response.success){
        alert(`${selectedItems.length}명의 사용자가 삭제되었습니다.`);
        
        // 목록 재조회
        handleSearch();
      }else{
        alert(response.message);
      }
      
    } catch (error) {
      console.error('선택된 사용자 삭제 실패:', error);
      
      let errorMessage = '사용자 삭제에 실패했습니다.';
      if (error.message) {
        errorMessage += `\n오류: ${error.message}`;
      }
      
      alert(errorMessage);
    }
  };

  return (
    <div className="admin">
      {/* 페이지 헤더 */}
      <div className="page-header">
        <h1>관리자</h1>
        <div className="main-tabs">
          <button
            className={`main-tab-btn ${activeMainTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveMainTab('users')}
          >
            사용자 관리
          </button>
          <button
            className={`main-tab-btn ${activeMainTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveMainTab('settings')}
          >
            설정
          </button>
        </div>
      </div>

      {/* 사용자 관리 탭 */}
      {activeMainTab === 'users' && (
        <>
          {/* 검색 영역 */}
          <div className="search-section">
        <div className="search-form">
          <div className="search-field">
            <label>이름</label>
            <input
              type="text"
              value={searchKeyword}
              onChange={handleSearchKeywordChange}
              onKeyPress={handleSearchKeyPress}
              placeholder="사용자 이름을 입력하세요"
            />
          </div>
          <button 
            className="search-btn"
            onClick={handleSearchClick}
            disabled={isSearching}
          >
            {isSearching ? '검색 중...' : '검색'}
          </button>
        </div>
      </div>

      {/* 사용자 목록 섹션 */}
      <div className="content-section list-section">
        <div className="section-header">
          <h2>사용자 목록</h2>
          <p>등록된 사용자를 조회하고 관리할 수 있습니다.</p>
        </div>

        {/* 목록 컨트롤 */}
        <div className="list-controls">
          <div className="list-info">
            총 {totalCount}명의 사용자 (페이지 {currentPage} / {totalPages})
            {selectedItems.length > 0 && (
              <span className="selected-count"> • {selectedItems.length}개 선택됨</span>
            )}
          </div>
          <div className="list-actions">
            <button 
              className="action-btn delete-btn"
              onClick={handleDeleteSelected}
              disabled={selectedItems.length === 0}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18"/>
                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
              </svg>
              선택 삭제
            </button>
            <button 
              className="action-btn add-btn"
              onClick={handleAddClick}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 5v14"/>
                <path d="M5 12h14"/>
              </svg>
              추가
            </button>
          </div>
        </div>

        {/* 사용자 목록 테이블 */}
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th className="checkbox-col">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
                <th>아이디</th>
                <th>이름</th>
                <th>등록일</th>
                <th className="action-col">삭제</th>
              </tr>
            </thead>
            <tbody>
              {users.length > 0 ? (
                users.map((user, index) => {
                  const userId = user.idx || user.id || index;
                  return (
                    <tr key={index}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(userId)}
                          onChange={(e) => handleSelectItem(userId, e.target.checked)}
                        />
                      </td>
                      <td>{user.account || user.userId || '-'}</td>
                      <td>{user.name || '-'}</td>
                      <td>{formatDate(user.regDate || user.createdAt)}</td>
                      <td>
                        <button 
                          className="table-action-btn delete-btn"
                          onClick={() => handleDeleteUser(user)}
                          title="삭제"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 6h18"/>
                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
                          </svg>
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="5" className="no-data">
                    {isSearching ? '검색 중...' : '등록된 사용자가 없습니다.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 페이징 */}
        {totalPages > 1 && (
          <div className="pagination">
            {renderPagination()}
          </div>
        )}
      </div>

      {/* 사용자 추가 모달 */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={handleModalClose}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>사용자 추가</h3>
              <button 
                className="close-btn"
                onClick={handleModalClose}
                title="닫기"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18"/>
                  <path d="M6 6l12 12"/>
                </svg>
              </button>
            </div>
            
            <div className="modal-body">
              <div className="modal-form">
                <div className="form-group">
                  <label>아이디 *</label>
                  <input
                    type="text"
                    value={modalForm.userId}
                    onChange={(e) => handleModalFormChange('userId', e.target.value)}
                    placeholder="아이디를 입력하세요"
                  />
                </div>
                
                <div className="form-group">
                  <label>이름 *</label>
                  <input
                    type="text"
                    value={modalForm.name}
                    onChange={(e) => handleModalFormChange('name', e.target.value)}
                    placeholder="이름을 입력하세요"
                  />
                </div>
                
                <div className="form-group">
                  <label>패스워드 *</label>
                  <input
                    type="password"
                    value={modalForm.password}
                    onChange={(e) => handleModalFormChange('password', e.target.value)}
                    placeholder="패스워드를 입력하세요"
                  />
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="cancel-btn"
                onClick={handleModalClose}
                disabled={isSubmitting}
              >
                취소
              </button>
              <button 
                className="submit-btn"
                onClick={handleModalSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? '추가 중...' : '추가'}
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}

      {/* 설정 탭 */}
      {activeMainTab === 'settings' && (
        <AdminSettings />
      )}
    </div>
  );
};

export default Admin;